import type {TemporaryAgencySerializer} from "@/generated/Company";
import {create} from "@/store/createStore";
import useRegistrationStore from "@/store/registration";
import type {Path, PathValue, TCurrency, TLearnAboutUsOption, TPackagePaymentPeriodType} from "@/store/types";
import {set as _set} from "lodash";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AddressSchema = z.object({
  address: z
    .string({message: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error")
    .superRefine((val, ctx) => {
      if (val.length && !/\d/.test(val)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "company_profile:missing_address_street_number_error",
        });
      }
    }),
  postal_code: z
    .string({message: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  city: z
    .string({message: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
});

const RegisterAgencySchema = z
  .object({
    agency_name: z.string().min(1, "multi_account:agency_name_required"),
    website: z.string().min(1, "Website is required").url("Website url is not valid"),
    first_name: z.string().min(1, "First name is required"),
    last_name: z.string().min(1, "Surname is required"),
  })
  .extend(AddressSchema.shape);

const DetailsSchema = z.object({
  phoneNumber: z.string().min(1, "incorrect_phone_number_length"),
});

const SetUpMultiAccountSchema = RegisterAgencySchema.omit({
  first_name: true,
  last_name: true,
});

export type TCreateAgencyFormState = Omit<
  TemporaryAgencySerializer,
  "country" | "currency" | "large_brand" | "package_type"
> & {
  country: string;
  currency: TCurrency;
  large_brand: boolean | null;
  package_type: TPackagePaymentPeriodType;
  place_description?: string | null;
  place_id: string | null;
};

type TDetailsState = {
  logo: File | null;
  profilePicture: File | null;
  username: string;
  password: string;
  howDidYouHearAboutUs: TLearnAboutUsOption | null;
  howDidYouHearAboutUsExtra: string;
  phoneNumber: string;
};

const initialFormState: TCreateAgencyFormState = {
  first_name: "",
  last_name: "",
  agency_name: "",
  address: "",
  city: "",
  postal_code: "",
  website: "",
  main_user_email: "",
  large_brand: null,
  country: "Switzerland",
  place_description: null,
  place_id: null,
  price_per_year: 0,
  price_per_month: 0,
  currency: "CHF",
  influencers_per_campaign: null,
  invitations_per_campaign: null,
  buyouts_per_year: null,
  number_of_campaigns: null,
  number_of_seats: null,
  package_type: "MONTHLY",
  sales_email: "",
  relationship_manager_email: "",
  extra_campaign_price: null,
  extra_seat_price: null,
};

const initialDetailsState: TDetailsState = {
  logo: null,
  profilePicture: null,
  username: "",
  password: "",
  howDidYouHearAboutUs: null,
  howDidYouHearAboutUsExtra: "",
  phoneNumber: "",
};

type TState = {
  form: TCreateAgencyFormState;
  details: TDetailsState;
  errors: Record<string, string[]> | null;
  setFormValue<K extends Path<TState["form"]>, V extends PathValue<TState["form"], K>>(key: K, value: V): void;
  setValue<K extends Path<TState>, V extends PathValue<TState, K>>(key: K, value: V): void;
  validateAddress(): boolean;
  validateRegisterAgencySchema(): boolean;
  validateSetUpMultiAccountSchema(): boolean;
  validateDetailsSchema(): boolean;
  setPhoneNumber(value: string): void;
  processErrorIssues(issues: z.ZodIssue[]): void;
  reset(): void;
};

const useCreateAgencyStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        form: initialFormState,
        details: initialDetailsState,
        errors: null,
        setFormValue: (key, value) => {
          get().setValue(("form." + key) as Path<TState>, value);
        },
        setValue: (key, value) => {
          set(
            state => {
              _set(state, key, value);
            },
            false,
            "useCreateAgencyStore/setFormValue",
          );

          const errorKey = key.split(".").pop()!;

          if (!!get().errors && errorKey in get().errors! && !!get().errors![errorKey].length) {
            set(state => {
              state.errors![errorKey] = [];
            });
          }
        },
        processErrorIssues: issues => {
          issues.map(issue =>
            set(state => {
              if (state.errors) {
                state.errors[issue.path[0]] = [issue.message];
              } else {
                state.errors = {[issue.path[0]]: [issue.message]};
              }
            }),
          );
        },
        validateAddress: () => {
          const result = AddressSchema.safeParse(get().form);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        validateRegisterAgencySchema: () => {
          useRegistrationStore.getState().validateCredentials();
          const result = RegisterAgencySchema.safeParse(get().form);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return get().validateDetailsSchema() && result.success && !useRegistrationStore.getState().errors.password;
        },
        validateDetailsSchema: () => {
          const result = DetailsSchema.safeParse(get().details);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        validateSetUpMultiAccountSchema: () => {
          const result = SetUpMultiAccountSchema.safeParse(get().form);

          if (!result.success) {
            get().processErrorIssues(result.error.issues);
          }

          return result.success;
        },
        setPhoneNumber: value => {
          set(
            state => {
              state.details.phoneNumber = value;

              if (state.errors) {
                state.errors.phoneNumber = [];
              }
            },
            false,
            "useCreateAgencyStore/setPhoneNumber",
          );
        },
        reset: () =>
          set(() => ({
            form: initialFormState,
            details: initialDetailsState,
            isSeats: false,
          })),
      }),
      {name: "useCreateAgencyStore"},
    ),
  ),
);

export {useCreateAgencyStore};
